import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import config from '../../config';
import { authFetch, ApiError, describeApiError } from '../../api/authFetch';
import { adminColors, radius } from '../../theme/adminTheme';

const CodeExecutionResult = ({ result, onRerun }) => {
  const [expandedPanels, setExpandedPanels] = useState({
    code: false,
    output: true,
    artifacts: true
  });

  const handlePanelChange = (panel) => (event, isExpanded) => {
    setExpandedPanels(prev => ({
      ...prev,
      [panel]: isExpanded
    }));
  };

  if (!result) return null;

  const { success, output, error, artifacts, execution_time, code } = result;

  return (
    <Paper
      elevation={2}
      sx={{
        mt: 2,
        mb: 2,
        border: `1px solid ${success ? adminColors.successBorder : adminColors.errorBorder}`,
        borderRadius: radius.surface,
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{
        p: 2,
        backgroundColor: success ? adminColors.successLight : adminColors.errorLight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CodeIcon color={success ? 'success' : 'error'} />
          <Typography variant="h6" color={success ? 'success.main' : 'error.main'}>
            {success ? '✅ Code Executed Successfully' : '❌ Code Execution Failed'}
          </Typography>
          {execution_time && (
            <Chip 
              icon={<TimerIcon />} 
              label={`${execution_time.toFixed(3)}s`} 
              size="small" 
              variant="outlined"
            />
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Re-run code">
            <IconButton onClick={onRerun} size="small">
              <PlayIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>



      {/* Code Section */}
      {code && (
        <Accordion 
          expanded={expandedPanels.code} 
          onChange={handlePanelChange('code')}
          sx={{ boxShadow: 'none' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              📝 Executed Code
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ 
              backgroundColor: '#f5f5f5', 
              p: 2, 
              borderRadius: radius.control,
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              overflow: 'auto',
              maxHeight: '300px'
            }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {code}
              </pre>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Output Section */}
      {(output || error) && (
        <Accordion 
          expanded={expandedPanels.output} 
          onChange={handlePanelChange('output')}
          sx={{ boxShadow: 'none' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              {success ? '📤 Output' : '🚨 Error Details'}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {success ? (
              <Box sx={{ 
                backgroundColor: '#f8f9fa', 
                p: 2, 
                borderRadius: radius.control,
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                overflow: 'auto',
                maxHeight: '400px',
                border: '1px solid #e9ecef'
              }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {output || 'No output produced'}
                </pre>
              </Box>
            ) : (
              <Alert severity="error" sx={{ fontFamily: 'monospace' }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {error}
                </pre>
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Artifacts Section */}
      {artifacts && artifacts.length > 0 && (
        <Accordion 
          expanded={expandedPanels.artifacts} 
          onChange={handlePanelChange('artifacts')}
          sx={{ boxShadow: 'none' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              📊 Generated Content ({artifacts.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {artifacts.map((artifact, index) => (
                <ArtifactDisplay key={index} artifact={artifact} />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Paper>
  );
};

const ArtifactDisplay = ({ artifact }) => {
  const { type, format, data, filename, name, shape, file_id, download_url, slides_count, size,
          pages_count, word_count, template_type } = artifact;
  const [downloadError, setDownloadError] = useState(null);

  const handleDownload = async () => {
    if ((type === 'presentation' || type === 'document') && download_url) {
      setDownloadError(null);
      try {
        // Construct full URL with base URL
        const fullDownloadUrl = download_url.startsWith('http')
          ? download_url
          : `${config.API_BASE_URL}${download_url}`;

        // Set appropriate content type based on format
        let acceptHeader = 'application/octet-stream';
        if (type === 'presentation') {
          acceptHeader = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
        } else if (type === 'document') {
          if (format === 'pdf') {
            acceptHeader = 'application/pdf';
          } else if (format === 'docx') {
            acceptHeader = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          }
        }
        
        // Make authenticated request to download the file
        const response = await authFetch(fullDownloadUrl, {
          method: 'GET',
          headers: {
            'Accept': acceptHeader
          }
        }, { timeoutMs: 60000 });

        if (response.status === 401) {
          window.location.href = '/signin';
          return;
        }

        if (!response.ok) {
          let detail;
          try {
            const data = await response.json();
            detail = data.detail;
          } catch (e) {
            // response body wasn't JSON; fall back to a generic message
          }
          throw new ApiError(response.status, detail);
        }

        // Get the file as a blob
        const blob = await response.blob();

        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'presentation.pptx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the blob URL
        URL.revokeObjectURL(url);

      } catch (error) {
        console.error('Download error:', error);
        setDownloadError(describeApiError(error));
      }
    } else if (type === 'image' && format === 'png') {
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${data}`;
      link.download = filename || 'plot.png';
      link.click();
    } else if (type === 'dataframe' && format === 'html') {
      const blob = new Blob([data], { type: 'text/html' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${name || 'dataframe'}.html`;
      link.click();
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {type === 'image' && '📊 Visualization'}
          {type === 'dataframe' && '📋 Data Table'}
          {type === 'presentation' && '📊 PowerPoint Presentation'}
          {type === 'document' && format === 'pdf' && '📄 PDF Document'}
          {type === 'document' && format === 'docx' && '📝 Word Document'}
          {filename && `: ${filename}`}
          {name && `: ${name}`}
          {shape && (
            <Chip 
              label={`${shape[0]} × ${shape[1]}`} 
              size="small" 
              variant="outlined" 
            />
          )}
          {slides_count && (
            <Chip 
              label={`${slides_count} slides`} 
              size="small" 
              variant="outlined" 
            />
          )}
          {pages_count && (
            <Chip 
              label={`${pages_count} pages`} 
              size="small" 
              variant="outlined" 
            />
          )}
          {word_count && (
            <Chip 
              label={`${word_count} words`} 
              size="small" 
              variant="outlined" 
            />
          )}
          {size && (
            <Chip 
              label={formatFileSize(size)} 
              size="small" 
              variant="outlined" 
            />
          )}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Download">
            <IconButton onClick={handleDownload} size="small">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {downloadError && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Alert severity="error" onClose={() => setDownloadError(null)}>
            {downloadError}
          </Alert>
        </Box>
      )}

      {type === 'image' && format === 'png' && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          p: 1,
          backgroundColor: '#fafafa',
          borderRadius: radius.control
        }}>
          <img 
            src={`data:image/png;base64,${data}`}
            alt={filename || 'Generated plot'}
            style={{ 
              maxWidth: '100%', 
              maxHeight: '400px',
              objectFit: 'contain'
            }}
          />
        </Box>
      )}

      {type === 'dataframe' && format === 'html' && (
        <Box sx={{ 
          maxHeight: '300px', 
          overflow: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: radius.control
        }}>
          <div dangerouslySetInnerHTML={{ __html: data }} />
        </Box>
      )}

      {type === 'presentation' && (
        <Box sx={{
          p: 2,
          backgroundColor: adminColors.accentLight,
          borderRadius: radius.control,
          textAlign: 'center'
        }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            🎯 PowerPoint presentation ready for download
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ mb: 1 }}
          >
            Download Presentation
          </Button>
          {slides_count && (
            <Typography variant="body2" color="text.secondary">
              {slides_count} slides • {formatFileSize(size)}
            </Typography>
          )}
        </Box>
      )}

      {type === 'document' && (
        <Box sx={{
          p: 2,
          backgroundColor: adminColors.accentLight,
          borderRadius: radius.control,
          textAlign: 'center'
        }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {format === 'pdf' ? '📄 PDF document ready for download' : '📝 Word document ready for download'}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ mb: 1 }}
          >
            Download {format === 'pdf' ? 'PDF' : 'Word Document'}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {pages_count && `${pages_count} pages`}
            {pages_count && word_count && ' • '}
            {word_count && `${word_count} words`}
            {(pages_count || word_count) && ' • '}
            {formatFileSize(size)}
            {template_type && ` • ${template_type}`}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const CodeExecutionIndicator = ({ isExecuting, requestType }) => {
  if (!isExecuting) return null;

  const getIndicatorText = (type) => {
    switch (type) {
      case 'data_analysis':
        return '📊 Analyzing data...';
      case 'visualization':
        return '📈 Creating visualization...';
      case 'calculation':
        return '🔢 Computing results...';
      case 'document_generation':
        return '📄 Generating document...';
      case 'presentation_generation':
        return '🎯 Creating PowerPoint presentation...';
      default:
        return '🐍 Executing Python code...';
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1,
      p: 2,
      backgroundColor: adminColors.accentLight,
      borderRadius: radius.surface,
      mb: 2
    }}>
      <CircularProgress size={20} />
      <Typography variant="body2" color="primary">
        {getIndicatorText(requestType)}
      </Typography>
    </Box>
  );
};

export { CodeExecutionResult, CodeExecutionIndicator };
export default CodeExecutionResult;
